import React, { useState } from "react";
import { connect } from "react-redux";
import { Button, Modal } from "antd";
import { Creators as PaymentCreators } from "../../services/redux/payments/actions";
import { isInRouteDeskFrame } from "../../util/helpers/deskPanelLook";
import { history } from "../../util/helpers/browserHistory";
import { DESK_PAYMENT_COPY, clockTime, deskPaymentScreen } from "../../util/helpers/deskPaymentStatus";
import { markFinalShown } from "../../services/sagas/payments/deskPayment";

// Desk build only, inside Route Desk's panel: the screen for a mobile-money
// payment being checked (services/sagas/payments/deskPayment.js). It lives
// at app level, not in the cart, so it is still there after a reload or a
// CHE switch. A definite answer for the cart's own CHE opens the app's
// normal result page instead. Outside a frame it renders nothing.

const TITLES = {
  waiting: "Waiting for the payment",
  unconfirmed: "Paid",
  timeout: "No answer yet",
  paid: "Payment successful",
  failed: "Payment unsuccessful",
};

const DeskPaymentStatus = ({ deskPayment, deskPaymentSet, deskPaymentClear, deskPaymentCheckAgain }) => {
  // The waiting screen the officer closed: { orderId, phase }.
  const [hidden, setHidden] = useState(null);
  if (!isInRouteDeskFrame()) return null;
  const screen = deskPaymentScreen(deskPayment, hidden);
  if (!screen) return null;

  const close = () => {
    if (screen === "waiting") {
      setHidden({ orderId: deskPayment.orderId, phase: deskPayment.phase });
    } else if (screen === "unconfirmed" || screen === "timeout") {
      markFinalShown(deskPayment);
      if (deskPayment.phase === "stale") deskPaymentClear(deskPayment.orderId);
      else deskPaymentSet({ ...deskPayment, finalShown: true });
    } else {
      // paid / failed shown by the panel: done with it (a paid marker keeps
      // blocking, but its screen is not shown again).
      markFinalShown(deskPayment);
      deskPaymentClear(deskPayment.orderId);
    }
  };

  let text;
  if (screen === "waiting") text = deskPayment.phase === "confirming" ? DESK_PAYMENT_COPY.confirming : DESK_PAYMENT_COPY.checking;
  else if (screen === "unconfirmed") text = DESK_PAYMENT_COPY.unconfirmed;
  else if (screen === "timeout") text = DESK_PAYMENT_COPY.timeout;
  else text = screen === "paid" ? DESK_PAYMENT_COPY.paidOther : DESK_PAYMENT_COPY.failedOther;

  const order = [deskPayment.orderName && `Order ${deskPayment.orderName}`, deskPayment.cheName]
    .filter(Boolean).join(" · ");

  return (
    <Modal visible footer={null} onCancel={close} maskClosable={false} width={340} centered
      className="desk-payment-status" bodyStyle={{ padding: 20, textAlign: "center" }}>
      {screen === "waiting" && (
        <img width="60px" height="60px" src="/images/loader-40.svg" alt="loading" />
      )}
      <h4 className="generic-modal-title desk-payment-title" style={{ marginTop: 10 }}>{TITLES[screen]}</h4>
      {order && <p className="desk-payment-order" style={{ marginBottom: 6 }}>{order}</p>}
      <p className="generic-modal-text desk-payment-text">{text}</p>
      {screen === "timeout" && deskPayment.errors > 0 && (
        <p className="desk-payment-errors">
          The server could not be reached {deskPayment.errors === 1 ? "once" : `${deskPayment.errors} times`}.
        </p>
      )}
      {(screen === "timeout" || screen === "unconfirmed") && deskPayment.checkedAgainAt && (
        <p className="desk-payment-checked-again">
          Checked again at {clockTime(deskPayment.checkedAgainAt)}: {screen === "unconfirmed" ? "the order is still not confirmed." : "still no answer."}
        </p>
      )}
      {(screen === "timeout" || screen === "unconfirmed") && deskPayment.phase !== "stale" && (
        <Button className="desk-payment-check-again" disabled={!!deskPayment.checkingAgain}
          onClick={() => deskPaymentCheckAgain(deskPayment.orderId)}
          style={{ marginRight: 8, marginBottom: 8 }}>Check again now</Button>
      )}
      {screen === "timeout" && (
        <Button className="desk-payment-orders" onClick={() => { close(); history.push("/orders"); }}
          style={{ marginRight: 8 }}>Open Orders</Button>
      )}
      <Button className="desk-payment-close" onClick={close}>Close</Button>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  deskPayment: state?.payment?.deskPayment,
});

const mapDispatchToProps = (dispatch) => ({
  deskPaymentSet: (deskPayment) => dispatch(PaymentCreators.deskPaymentSet(deskPayment)),
  deskPaymentClear: (orderId) => dispatch(PaymentCreators.deskPaymentClear(orderId)),
  deskPaymentCheckAgain: (orderId) => dispatch(PaymentCreators.deskPaymentCheckAgain(orderId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeskPaymentStatus);
